import api from './api';

// ── TypeScript Interfaces ──

export interface SchoolPerformanceParams {
  year?: number;
  district?: string;
  level?: number;
}

export interface SchoolPerformanceSchool {
  school_reg_number: string;
  school_name: string;
  district_name: string;
  level_name: string;
  current_enrollment: number;
  teacher_count: number;
  student_attendance_rate: number | null;
  teacher_attendance_rate: number | null;
  enrollment_growth: number;
  student_teacher_ratio: number | null;
  infrastructure_score: number;
  composite_score: number;
  rank: number;
  category: string;
  normalized: {
    student_attendance: number;
    teacher_attendance: number;
    enrollment_stability: number;
    student_teacher_ratio: number;
    infrastructure: number;
  };
}

export interface SchoolPerformanceData {
  year: number;
  summary: {
    total_schools: number;
    avg_composite_score: number;
    excellent_count: number;
    good_count: number;
    average_count: number;
    needs_improvement_count: number;
  };
  schools: SchoolPerformanceSchool[];
}

export interface GradeFlowParams {
  year?: number;
  level?: number;
  district?: string;
}

export interface GradeMetric {
  grade_code: number;
  grade_label: string;
  total_students: number;
  promoted: number;
  repeated: number;
  dropped_out: number;
  new_entrants: number;
  promotion_rate: number;
  repetition_rate: number;
  dropout_rate: number;
}

export interface FlowLink {
  from: string;
  to: string;
  from_code: number;
  to_code: number;
  value: number;
}

export interface TransitionData {
  completers: number;
  transitioned: number;
  rate: number;
}

export interface GradeFlowData {
  year: number;
  level: string;
  summary: {
    total_students: number;
    avg_promotion_rate: number;
    avg_repetition_rate: number;
    avg_dropout_rate: number;
    bottleneck_grade: string | null;
    bottleneck_dropout_rate: number;
  };
  grade_metrics: GradeMetric[];
  flow_data: FlowLink[];
  transitions: {
    primary_to_secondary?: TransitionData;
    pre_primary_to_primary?: TransitionData;
  };
}

export interface GenderEquityParams {
  year?: number;
  district?: string;
  level?: number;
}

export interface GradeGenderGap {
  grade_code: number;
  grade_label: string;
  female: number;
  male: number;
  gpi: number;
  gap: number;
}

export interface DistrictGPIRanking {
  district_name: string;
  female: number;
  male: number;
  gpi: number;
  gap: number;
  total: number;
  rank: number;
  status: string;
}

export interface GPITrend {
  year: number;
  gpi_overall: number;
  total_female: number;
  total_male: number;
  [key: string]: number; // dynamic level keys like gpi_primary, gpi_secondary
}

export interface GenderEquityData {
  year: number;
  summary: {
    overall_gpi: number;
    gpi_status: string;
    total_female: number;
    total_male: number;
    gender_gap: number;
    gap_percentage: number;
  };
  gpi_trends: GPITrend[];
  grade_gender_gap: GradeGenderGap[];
  district_ranking: DistrictGPIRanking[];
}

// ── Advanced Analytics Interfaces ──

export interface EnrollmentForecastData {
  historical: { year: number; enrollment: number; school_count: number }[];
  forecasts: { year: number; enrollment: number; is_forecast: boolean }[];
  summary: {
    current_enrollment: number;
    projected_next_year: number;
    annual_growth_rate: number;
    schools_declining: number;
    total_schools: number;
    years_of_data: number;
  };
  school_forecasts: {
    school_reg_number: string;
    school_name: string;
    district_name: string;
    current_enrollment: number;
    projected_enrollment: number;
    annual_growth_rate: number;
    trend: string;
  }[];
  declining_schools: any[];
}

export interface AttendanceClusterData {
  year: number;
  summary: {
    total_schools: number;
    overall_attendance_rate: number;
    regular_pct: number;
    chronic_pct: number;
  };
  cluster_distribution: {
    cluster: string;
    count: number;
    percentage: number;
    avg_attendance_rate: number;
    color: string;
  }[];
  day_of_week_student: { day: string; day_index: number; attendance_rate: number }[];
  day_of_week_teacher: { day: string; day_index: number; attendance_rate: number }[];
  schools: any[];
}

export interface TeacherImpactData {
  year: number;
  summary: {
    total_schools: number;
    avg_teacher_attendance: number;
    total_learning_days_lost: number;
    correlation_coefficient: number;
    worst_school: string | null;
  };
  schools: {
    school_reg_number: string;
    school_name: string;
    district_name: string;
    teacher_count: number;
    teacher_attendance_rate: number;
    absent_teacher_days: number;
    student_enrollment: number;
    learning_days_lost: number;
    student_attendance_rate: number | null;
  }[];
  correlation_data: { teacher_rate: number; student_rate: number; school_name: string }[];
  monthly_trend: { month: string; month_num: number; attendance_rate: number }[];
}

export interface InfrastructureCorrelationData {
  year: number;
  summary: {
    total_schools: number;
    pct_with_power: number;
    pct_with_water: number;
    pct_with_library: number;
    avg_rooms: number;
  };
  factors: {
    factor: string;
    with_count: number;
    without_count: number;
    with_attendance: number;
    without_attendance: number;
    attendance_difference: number;
    with_avg_enrollment?: number;
    without_avg_enrollment?: number;
  }[];
  schools: any[];
}

export interface SeasonalPatternData {
  summary: {
    best_month: string;
    best_month_rate: number;
    worst_month: string;
    worst_month_rate: number;
    best_day: string;
    best_day_rate: number;
    worst_day: string;
    worst_day_rate: number;
  };
  monthly_student: { year: number; month: string; month_num: number; attendance_rate: number }[];
  monthly_teacher: { year: number; month: string; month_num: number; attendance_rate: number }[];
  dow_student: { day: string; day_index: number; student_attendance_rate: number }[];
  dow_teacher: { day: string; day_index: number; teacher_attendance_rate: number }[];
  heatmap: { month: string; month_num: number; day: string; day_index: number; attendance_rate: number }[];
}

export interface StaffingData {
  year: number;
  summary: {
    total_schools: number;
    avg_student_teacher_ratio: number;
    understaffed_count: number;
    well_staffed_count: number;
    no_teachers_count: number;
    critical_count: number;
  };
  str_distribution: { range: string; count: number }[];
  qualifications: { qualification: string; count: number; color: string }[];
  experience: { bucket: string; count: number }[];
  gender_distribution: { gender: string; count: number }[];
  schools: {
    school_reg_number: string;
    school_name: string;
    district_name: string;
    teacher_count: number;
    enrollment: number;
    student_teacher_ratio: number;
    status: string;
  }[];
}

export interface AnomalyData {
  year: number;
  summary: {
    total_anomalies: number;
    critical_count: number;
    warning_count: number;
    data_quality_flags: number;
  };
  by_type: { type: string; count: number }[];
  anomalies: {
    type: string;
    description: string;
    severity: string;
    z_score: number;
    school_reg_number: string;
    school_name: string;
    district_name: string;
  }[];
}

export interface DistrictBenchmarkData {
  year: number;
  summary: {
    total_districts: number;
    top_district: string | null;
    bottom_district: string | null;
    national_avg_str: number;
  };
  national_averages: Record<string, number>;
  districts: {
    district_name: string;
    school_count: number;
    total_enrollment: number;
    total_teachers: number;
    student_teacher_ratio: number;
    gpi: number;
    teacher_attendance_rate?: number;
    infrastructure_score?: number;
    student_attendance_rate?: number;
    enrollment_growth?: number;
    rank: number;
    overall_rank: number;
    [key: string]: any;
  }[];
  metrics: string[];
}

export interface CohortSurvivalData {
  start_year: number;
  level: string;
  cohort_size: number;
  summary: {
    starting_cohort: number;
    current_survivors: number;
    estimated_completion_rate: number;
    leakiest_transition: { from: string; to: string; students_lost: number } | null;
    years_tracked: number;
  };
  grade_survival: {
    year: number;
    grade_code: number;
    grade_label: string;
    cohort_enrolled: number;
    on_track: number;
    repeating: number;
    dropped_out: number;
    attrition_this_year: number;
    survival_rate: number;
    on_track_rate: number;
  }[];
  district_comparison: {
    district_name: string;
    cohort_size: number;
    still_enrolled: number;
    survival_rate: number;
  }[];
}

// ── API Functions ──

export const learningAnalyticsAPI = {
  // Core features
  getSchoolPerformance: (params?: SchoolPerformanceParams) =>
    api.get('/ml-analytics/learning/school-performance/', { params }),

  getGradeFlow: (params?: GradeFlowParams) =>
    api.get('/ml-analytics/learning/grade-flow/', { params }),

  getGenderEquity: (params?: GenderEquityParams) =>
    api.get('/ml-analytics/learning/gender-equity/', { params }),

  // Advanced features
  getEnrollmentForecast: (params?: { level?: number; district?: string }) =>
    api.get('/ml-analytics/learning/enrollment-forecast/', { params }),

  getAttendanceClusters: (params?: { year?: number; district?: string; level?: number }) =>
    api.get('/ml-analytics/learning/attendance-clusters/', { params }),

  getTeacherImpact: (params?: { year?: number; district?: string }) =>
    api.get('/ml-analytics/learning/teacher-impact/', { params }),

  getInfrastructureCorrelation: (params?: { year?: number; district?: string; level?: number }) =>
    api.get('/ml-analytics/learning/infrastructure-correlation/', { params }),

  getSeasonalPatterns: (params?: { district?: string; level?: number }) =>
    api.get('/ml-analytics/learning/seasonal-patterns/', { params }),

  getStaffingAnalytics: (params?: { year?: number; district?: string; level?: number }) =>
    api.get('/ml-analytics/learning/staffing-analytics/', { params }),

  getAnomalyDetection: (params?: { year?: number; district?: string }) =>
    api.get('/ml-analytics/learning/anomaly-detection/', { params }),

  getDistrictBenchmarking: (params?: { year?: number; level?: number }) =>
    api.get('/ml-analytics/learning/district-benchmarking/', { params }),

  getCohortSurvival: (params?: { start_year?: number; level?: number; district?: string }) =>
    api.get('/ml-analytics/learning/cohort-survival/', { params }),
};
